import m from 'mithril';

import { TermComparison } from './term_comparison.ts';
import { asCurrency, asPercent } from './format.ts';
import { NumberInput } from './input.ts';
import { Page } from './layout.ts';
import type { Cost } from './types.ts';
import type { Mortgage } from './lib/mortgage.ts';
import {
    UnaffordableMortgageError,
    findMortgage,
    monthlyCost,
    totalCost,
    totalInterest,
} from './lib/mortgage.ts';
import {
    Bank,
    mortgageDetail,
    typicalMortgageDetail,
} from './lib/mortgage_detail.ts';
import { RATES_AS_AT } from './lib/rates.ts';

/**
 * The fields start on a real two year fix at 75% LTV and the rate it reverts
 * to, rather than on numbers somebody typed in once. Both are yours to change —
 * this is only a better place to start than a guess going stale.
 */
const TYPICAL = typicalMortgageDetail(2);

/** 0.0479 as 4.79: the fields are in percent, and floats are not. */
function asRateField(fraction: number) {
    return Number((fraction * 100).toFixed(2));
}

/**
 * The worked example under the graph, costed rather than written down.
 *
 * It used to quote £1,348 a month and £12,959 of interest at 3.59%, from
 * whenever somebody worked it out — wrong twice over by the time anyone
 * noticed, once because the rate had moved and once because the £10 search
 * step was out by a few pounds. Running it means the sentence cannot say
 * anything the app would not.
 */
const EXAMPLE_PRINCIPAL = 100000;
const EXAMPLE_YEARS = 7;
const EXAMPLE = findMortgage(
    mortgageDetail(
        Bank.MARKET,
        EXAMPLE_YEARS,
        TYPICAL.initial_interest,
        TYPICAL.initial_interest,
        0,
    ),
    EXAMPLE_YEARS * 12,
    EXAMPLE_PRINCIPAL,
);

/** A term the sweep found a mortgage for, and the mortgage itself. */
interface Term {
    cost: Cost;
    mortgage: Mortgage;
}

/**
 * Computed in the browser rather than fetched. A full sweep of every term is a
 * few milliseconds, so there is nothing to debounce and no in-flight state to
 * indicate — the numbers are simply always current with the inputs.
 *
 * The mortgage is kept alongside the row it was flattened into, rather than
 * thrown away and found again when the schedule wants it. It has already been
 * computed, and a second call would be a second set of figures to keep honest.
 */
function termsFor(inputs: typeof model.inputs): Term[] {
    const { principal, fixed_term, fixed_rate, variable_rate, max_term } =
        inputs;
    const lower_term = 2;

    // Clearing an input gives NaN, which would loop forever below.
    if (!Number.isFinite(max_term) || max_term < lower_term) {
        return [];
    }

    const detail = mortgageDetail(
        Bank.EMPTY,
        fixed_term,
        fixed_rate * 0.01,
        variable_rate * 0.01,
        0,
    );

    const terms: Term[] = [];
    for (let term = lower_term; term <= max_term; term++) {
        try {
            const mortgage = findMortgage(detail, term * 12, principal);
            terms.push({
                mortgage,
                cost: {
                    term,
                    monthly_cost: monthlyCost(mortgage),
                    total_cost: totalCost(mortgage),
                    loan_cost: totalInterest(mortgage),
                },
            });
        } catch (error) {
            // The old /mortgages endpoint dropped unaffordable terms silently.
            if (!(error instanceof UnaffordableMortgageError)) {
                throw error;
            }
        }
    }

    return terms;
}

/**
 * The term whose schedule is open. 25 years to begin with, the length most
 * mortgages are sold in; after that, whatever was last picked.
 *
 * A term can stop existing under you — shrink the maximum, or raise the rates
 * until the long end is unaffordable — so the selection is the nearest term
 * still on the page rather than a number that no longer means anything.
 */
function nearestTerm(terms: Term[], wanted: number) {
    if (terms.length === 0) {
        return null;
    }
    return terms.reduce(
        (best, { cost }) =>
            Math.abs(cost.term - wanted) < Math.abs(best - wanted)
                ? cost.term
                : best,
        terms[0].cost.term,
    );
}

const model = {
    inputs: {
        principal: 100000,
        fixed_term: TYPICAL.fixed_term,
        fixed_rate: asRateField(TYPICAL.initial_interest),
        variable_rate: asRateField(TYPICAL.subsequent_interest),
        max_term: 35,
    },
    terms: [] as Term[],
    // Held rather than derived in the view: the graph redraws when this array
    // stops being the same one, so a fresh copy every redraw would redraw the
    // chart on every keystroke anywhere on the page.
    costs: [] as Cost[],
    selected_term: 25 as number | null,
    refresh: function () {
        this.terms = termsFor(this.inputs);
        this.costs = this.terms.map((term) => term.cost);
        this.selected_term = nearestTerm(this.terms, this.selected_term ?? 25);
    },
    selectedMortgage: function () {
        const found = this.terms.find(
            ({ cost }) => cost.term === this.selected_term,
        );
        return found ? found.mortgage : null;
    },
    setInput: function (field: keyof typeof model.inputs, value: number) {
        this.inputs[field] = value;
        this.refresh();
    },
};

const VariableRateInput = {
    view: ({ attrs: { value } }) =>
        m(NumberInput, {
            value,
            onchange: (x) => model.setInput('variable_rate', x),
            step: 0.01,
            chars: 5,
            id: 'variable_rate',
            text: 'The variable rate is',
            afterText: '%',
        }),
};

const FixedRateInput = {
    view: ({ attrs: { value } }) =>
        m(NumberInput, {
            value,
            onchange: (x) => model.setInput('fixed_rate', x),
            step: 0.01,
            chars: 5,
            id: 'fixed_rate',
            text: 'The fixed rate is',
            afterText: '%',
        }),
};

const PrincipalInput = {
    view: ({ attrs: { value } }) =>
        m(NumberInput, {
            value,
            onchange: (x) => model.setInput('principal', x),
            step: 10000,
            chars: 9,
            id: 'principal',
            text: 'I want to borrow',
            prefix: '£',
            group: true,
        }),
};

const FixedTermInput = {
    view: ({ attrs: { value } }) =>
        m(NumberInput, {
            value,
            onchange: (x) => model.setInput('fixed_term', x),
            chars: 3,
            id: 'fixed_term',
            text: 'The fixed term is',
            afterText: 'years',
        }),
};

const MaxTermInput = {
    view: ({ attrs: { value } }) =>
        m(NumberInput, {
            value,
            onchange: (x: number) => model.setInput('max_term', x),
            chars: 3,
            id: 'max_term',
            text: 'The maximum term I want is',
            afterText: 'years',
        }),
};

export const TermComparisonPage = {
    oninit: () => model.refresh(),
    view: () =>
        m(
            Page,
            { title: 'Total cost vs monthly repayment' },
            m(
                'p.lede',
                "Mortgages are normally advertised in terms of length and APR, and neither is that useful when you are choosing between them. What you care about is the total interest you pay over the whole mortgage, and the monthly repayment you'll have to make.",
            ),
            m(
                '.controls',
                m(PrincipalInput, { value: model.inputs.principal }),
                m(FixedTermInput, { value: model.inputs.fixed_term }),
                m(FixedRateInput, { value: model.inputs.fixed_rate }),
                m(VariableRateInput, { value: model.inputs.variable_rate }),
                m(MaxTermInput, { value: model.inputs.max_term }),
            ),
            m(TermComparison, {
                costs: model.costs,
                terms: model.terms.map(({ cost }) => cost.term),
                selected_term: model.selected_term,
                mortgage: model.selectedMortgage(),
                onpick: (term: number) => {
                    model.selected_term = term;
                },
            }),
            m(
                '.notes',
                m(
                    'p.prose',
                    'Each circle is one length of mortgage, labelled with its term in years. Every circle costs you the same principal — pick one by deciding how much you are willing to pay each month, and the graph tells you what that choice costs in interest. Switch to the table for exact figures.',
                ),
                m(
                    'p.prose',
                    'Monthly repayment is what you pay the lender each month.',
                ),
                m(
                    'p.prose',
                    `The rates above start on what lenders were quoting in ${RATES_AS_AT} for a two year fix at 75% loan to value, and what it reverts to afterwards — the Bank of England's average of the market. Change them to whatever you have actually been offered.`,
                ),
                m(
                    'p.prose',
                    `Total cost is what you repay beyond the principal, because of the interest charged. Borrow ${asCurrency(
                        EXAMPLE_PRINCIPAL,
                    )} at ${asPercent(
                        TYPICAL.initial_interest,
                    )} and repay ${asCurrency(
                        monthlyCost(EXAMPLE),
                    )} a month to clear it in ${EXAMPLE_YEARS} years, and you pay back the ${asCurrency(
                        EXAMPLE_PRINCIPAL,
                    )} plus ${asCurrency(
                        totalInterest(EXAMPLE),
                    )} of interest — that ${asCurrency(
                        totalInterest(EXAMPLE),
                    )} is the total cost.`,
                ),
            ),
        ),
};
